import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { AdminActionView, CursorPageResponse } from '../../types/offeringsV2';

interface NotificationPanelProps {
  token: string | null;
  isOpen: boolean;
  onClose: () => void;
  onMarkRead: () => void;
}

function actionBadgeClass(action: AdminActionView['action']): string {
  if (action === 'OFFERING_APPROVED' || action === 'VARIANT_APPROVED' || action === 'OFFER_APPROVED') return 'notif-panel__badge notif-panel__badge--approved';
  if (action === 'OFFERING_REJECTED' || action === 'VARIANT_REJECTED' || action === 'OFFER_REJECTED' || action === 'OFFER_SUSPENDED') return 'notif-panel__badge notif-panel__badge--rejected';
  if (action === 'OFFER_ENDED') return 'notif-panel__badge notif-panel__badge--ended';
  if (action === 'OFFER_EDITED') return 'notif-panel__badge notif-panel__badge--info';
  return 'notif-panel__badge notif-panel__badge--changes';
}

function actionLabel(action: AdminActionView['action']): string {
  switch (action) {
    case 'OFFER_APPROVED': return 'Offer approved';
    case 'OFFER_REJECTED': return 'Offer rejected';
    case 'OFFER_SUSPENDED': return 'Offer suspended';
    case 'OFFER_ENDED': return 'Offer ended';
    case 'OFFER_EDITED': return 'Offer edited by Garagat';
    default: break;
  }
  if (action === 'OFFERING_APPROVED' || action === 'VARIANT_APPROVED') return 'Approved';
  if (action === 'OFFERING_REJECTED' || action === 'VARIANT_REJECTED') return 'Rejected';
  return 'Changes requested';
}

/** Spec 062: offer rows open the offer; everything else opens Services (unchanged). */
function targetPath(item: AdminActionView): string {
  if (item.action.startsWith('OFFER_')) return item.offerId ? `/promotions/${item.offerId}` : '/promotions';
  return '/services';
}

function NotificationPanel({ token, isOpen, onClose, onMarkRead }: NotificationPanelProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminActionView[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const markedRead = useRef(false);

  const loadPage = useCallback(async (cursor: number | null, replace: boolean) => {
    if (!token) return;
    setLoading(true);
    try {
      const page: CursorPageResponse<AdminActionView> = await api.getAdminActionFeed(token, cursor, 20);
      const rows = page.content ?? [];
      setItems((prev) => replace ? rows : [...prev, ...rows]);
      setNextCursor(page.nextCursor);
      setHasMore(page.nextCursor != null);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isOpen) return;
    markedRead.current = false;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    loadPage(null, true);

    if (token && !markedRead.current) {
      markedRead.current = true;
      api.markAdminActionsRead(token).catch(() => {});
      onMarkRead();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadPage(nextCursor, false);
    }
  }, [loading, hasMore, nextCursor, loadPage]);

  return (
    <>
      {isOpen && <div className="notif-panel__overlay" onClick={onClose} aria-hidden="true" />}
      <div className={`notif-panel${isOpen ? ' notif-panel--open' : ''}`} role="dialog" aria-label="Notifications" aria-modal="true">
        <div className="notif-panel__header">
          <span className="notif-panel__title">Updates</span>
          <button className="notif-panel__close" onClick={onClose} aria-label="Close notifications">&times;</button>
        </div>
        <div className="notif-panel__list" ref={listRef} onScroll={handleScroll}>
          {loading && items.length === 0 && (
            <div className="notif-panel__loading">
              <div className="notif-panel__spinner" />
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="notif-panel__empty">No updates yet.</div>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              className="notif-panel__row"
              role="button"
              tabIndex={0}
              onClick={() => { onClose(); navigate(targetPath(item)); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { onClose(); navigate(targetPath(item)); } }}
            >
              <div className="notif-panel__row-top">
                <span className={actionBadgeClass(item.action)}>{actionLabel(item.action)}</span>
                <span className="notif-panel__time">{new Date(item.occurredAt).toLocaleString()}</span>
              </div>
              <div className="notif-panel__detail">
                {item.offerTitle && <span className="notif-panel__offering">{item.offerTitle}</span>}
                {item.offeringName && <span className="notif-panel__offering">{item.offeringName}</span>}
                {item.variantLabel && <span className="notif-panel__variant"> · {item.variantLabel}</span>}
              </div>
              {item.reason && <div className="notif-panel__reason">{item.reason}</div>}
            </div>
          ))}
          {loading && items.length > 0 && (
            <div className="notif-panel__loading">
              <div className="notif-panel__spinner" />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default NotificationPanel;
